export const HOME_FEATURES = [
  {
    title: 'No sign-up required',
    text: 'Start using the tool immediately. No email, no password, no account.',
  },
  {
    title: 'Free outputs include a watermark',
    text: 'Free downloads are clean and ready to use, with a small watermark. Pro removes the watermark.',
  },
  {
    title: 'Up to 4K with Pro (planned)',
    text: 'Upgrade to Pro for higher-resolution output and larger upscales when they become available.',
  },
  {
    title: 'Privacy-focused',
    text: 'Your image is processed for the job and removed afterward.',
  },
  {
    title: 'Works on any device',
    text: 'Upload from desktop, tablet, or phone.',
  },
  {
    title: 'Fast AI processing',
    text: 'Most images finish in a few seconds.',
  },
];

export const USE_CASES = ['Social media graphics', 'E-commerce product photos', 'Portraits and profile pictures', 'Prints and posters'];

export const TRUST_BAR = ['Free daily quota: 2 images/day', 'JPG, PNG, WebP supported', 'Up to 5MB per upload'];

export const HOME_FAQ = [
  {
    question: 'Is Image Enlarger free to use?',
    answer:
      'Yes. You can upscale up to 2 images per day at no cost, with no sign-up. Free outputs include a watermark; Pro is watermark-free.',
  },
  {
    question: 'What image formats are supported?',
    answer: 'JPG, PNG, and WebP are supported. Output matches the original format.',
  },
  {
    question: 'Do I need to create an account?',
    answer:
      'No. The free tier works without registration. Pro upgrades can be managed without a traditional login in the first version.',
  },
  {
    question: 'What is the difference between Free and Pro?',
    answer:
      'Free includes 2 images/day at 2× resolution. Pro includes 100 images/month, 2× and 4× upscales, and higher-resolution output.',
  },
  {
    question: 'Is my image stored permanently?',
    answer: 'No. Images are temporarily stored for processing and deleted as soon as the job is complete.',
  },
];

export const AI_UPSCALER_FAQ = [
  {
    question: 'What does an AI image upscaler do?',
    answer:
      'It uses AI to enlarge an image while trying to preserve sharpness and detail, instead of simply stretching pixels.',
  },
  {
    question: 'Is this AI image upscaler free?',
    answer: 'Yes, you can upscale 2 images per day for free. Pro users get 100 images per month and 4× output.',
  },
  {
    question: 'Can I use AI upscaling for commercial images?',
    answer:
      'You may use it on images you have the right to use. You are responsible for complying with copyright and licensing rules.',
  },
  {
    question: 'How large can I upscale an image?',
    answer: 'Free users get 2×. Pro users can choose 2× or 4× for higher-resolution results.',
  },
  {
    question: 'Does AI upscaling add fake details?',
    answer:
      'AI predicts what detail might look like. Results vary by image. It is not a replacement for a true high-resolution source.',
  },
];

export const AI_ENLARGER_FAQ = [
  {
    question: 'What is an AI image enlarger?',
    answer:
      'An AI image enlarger increases the dimensions of an image using AI to help maintain clarity and reduce pixelation.',
  },
  {
    question: 'Can I enlarge a photo for printing?',
    answer:
      'Yes. For best results, start with the highest-quality source you have. Pro users can enlarge up to 4× for larger prints.',
  },
  {
    question: 'Is the free image enlarger really free?',
    answer:
      'Yes, 2 images per day are free, with no sign-up. Free outputs include a watermark; Pro is watermark-free.',
  },
  {
    question: 'Will enlarging a photo make it blurry?',
    answer:
      'AI enlargement helps reduce blur compared to standard resizing, but it cannot create detail that does not exist in the original.',
  },
  {
    question: 'What formats can I enlarge?',
    answer: 'JPG, PNG, and WebP.',
  },
];

export const FREE_FAQ = [
  {
    question: 'Is this free image upscaler really free?',
    answer:
      'Yes. You can upscale 2 images per day for free. No credit card and no sign-up required.',
  },
  {
    question: 'Does the free version add a watermark?',
    answer: 'Yes. Free outputs include a small watermark. Pro users get watermark-free output.',
  },
  {
    question: 'What is the free daily limit?',
    answer: '2 images per day. The limit resets every 24 hours.',
  },
  {
    question: 'Can I use the free upscaler for commercial images?',
    answer:
      'You may use it on images you own or have the right to use. You are responsible for copyright compliance.',
  },
  {
    question: 'How do I get more than 2 free upscales?',
    answer: 'Upgrade to Pro for 100 images per month and higher-resolution output.',
  },
];

export const FOUR_K_FAQ = [
  {
    question: 'Can I really upscale to 4K for free?',
    answer:
      'No. The free plan only supports 2× upscales. 4× upscales, which can produce output closer to 4K, are a planned Pro feature.',
  },
  {
    question: 'Is 8K upscaling available?',
    answer: '8K upscaling is not available at launch. It is a planned future Pro feature and depends on the original image resolution.',
  },
  {
    question: 'What is 4K image resolution?',
    answer: '4K usually means 3840 × 2160 pixels. Reaching that size requires both a high enough upscale factor and a good-quality original file.',
  },
  {
    question: 'Do 4K upscales work on phones?',
    answer: 'The tool works on desktop and mobile browsers. However, the free plan only supports 2× upscales regardless of device.',
  },
  {
    question: 'Will a 4K upscale look exactly like a real 4K photo?',
    answer:
      'AI upscaling improves the appearance, but it cannot match a true 4K capture. Results depend on the quality of the original image and the upscale factor.',
  },
];

export const PHOTO_FAQ = [
  {
    question: 'Can I enlarge a photo online for free?',
    answer:
      'Yes. You can enlarge 2 photos per day for free. Free outputs include a watermark; Pro is watermark-free.',
  },
  {
    question: 'What photo formats work?',
    answer: 'JPG, PNG, and WebP are supported.',
  },
  {
    question: 'Can I enlarge a photo for a large print?',
    answer: 'Yes. Pro users can enlarge up to 4× for larger prints. Free users can enlarge 2×.',
  },
  {
    question: 'Will my photo stay private?',
    answer: 'Yes. Photos are temporarily stored for processing and removed afterward.',
  },
  {
    question: 'Can I enlarge multiple photos at once?',
    answer: 'Batch upload is a Pro placeholder feature in the first version.',
  },
];

export const QUALITY_FAQ = [
  {
    question: 'Can you really enlarge an image without losing quality?',
    answer:
      'AI upscaling can reduce quality loss, but it cannot fully replace a true high-resolution source. Results depend on the original image.',
  },
  {
    question: 'What is the best way to enlarge an image?',
    answer:
      'Start with the best original file, avoid over-compression, and use AI upscaling instead of simple stretching.',
  },
  {
    question: 'Is AI upscaling better than Photoshop resizing?',
    answer:
      'For quick, automatic results, AI upscaling is often faster and easier. Photoshop gives more manual control but takes more time and skill.',
  },
  {
    question: 'Can I enlarge a low-resolution image to 4K?',
    answer:
      'You can upscale it to 4K dimensions, but the result will look best if the original has enough detail. Pro users get 4× upscales.',
  },
  {
    question: 'Is this tool free?',
    answer: 'Yes, 2 images per day are free. Upgrade to Pro for more resolution and more images.',
  },
];

export const FAQ_PAGE_ITEMS = [
  {
    question: 'What is Image Enlarger?',
    answer:
      'Image Enlarger is an online AI tool that upscales and enlarges images. It is designed to be fast, simple, and free to try.',
  },
  {
    question: 'Is Image Enlarger free?',
    answer:
      'Yes. The free plan includes 2 image upscales per day, with no sign-up. Free outputs include a watermark; Pro is watermark-free.',
  },
  {
    question: 'What formats are supported?',
    answer: 'JPG, PNG, and WebP.',
  },
  {
    question: 'Do I need an account?',
    answer:
      'No. The free tool works without registration. The first version of Pro does not require a traditional login either.',
  },
  {
    question: 'How many images can I process?',
    answer:
      'Free users get 2 per day at 2× resolution, with watermarked output. Pro users get 100 per month and planned higher-resolution, watermark-free 4×/8× output.',
  },
  {
    question: 'What is the difference between 2× and 4× upscales?',
    answer:
      '2× doubles the width and height. 4× quadruples them, producing a much larger image. 4× is available on Pro.',
  },
  {
    question: 'Can I use the images commercially?',
    answer:
      'You may use images you own or have rights to use. You are responsible for copyright and licensing compliance.',
  },
  {
    question: 'Is my image stored?',
    answer: 'No. Images are temporarily stored for processing and deleted afterward. We do not use them for AI training.',
  },
  {
    question: 'Does the free plan add a watermark?',
    answer: 'Yes. Free outputs include a small watermark. Pro users get watermark-free output.',
  },
  {
    question: 'How do I upgrade to Pro?',
    answer:
      'Click the Pro or Upgrade button on any page, choose Monthly or Yearly, and follow the checkout flow (v1 placeholder UI).',
  },
];

export const PRICING_FAQ = [
  {
    question: 'How much does Image Enlarger cost?',
    answer: 'The free plan is $0. Pro Monthly is $9.90 and Pro Yearly is $94.80.',
  },
  {
    question: 'Is there a free trial?',
    answer: 'The free plan lets you upscale 2 images per day without a credit card.',
  },
  {
    question: 'What do I get with Pro?',
    answer:
      'Pro includes 100 images per month, higher-resolution watermark-free output, and planned 4×/8× upscales and batch processing. Real payment is not connected yet in the first version.',
  },
  {
    question: 'Can I cancel Pro?',
    answer: 'Yes, you can cancel anytime. Specific cancellation steps depend on your payment provider.',
  },
  {
    question: 'Is there an unlimited plan?',
    answer: 'No. Pro is capped at 100 images per month in the current plan.',
  },
];
